import { create } from 'zustand';
import type {
  Block,
  BlockType,
  Workbook,
  SourceLanguageCode,
  TargetLanguageCode,
  CefrLevelCode,
  PaceCode,
  TopicCode,
  SituationCode,
  LearningFocusCode,
  AIProviderCode,
  LearningReasonCode,
} from '@ellie/shared';
import { DEFAULT_AI_PROVIDER, DEFAULT_AI_MODEL, AI_MODELS } from '@ellie/shared';
import { supabase, invokeFunction } from '@/lib/supabase';

// =============================================================================
// Studio profile — the inline profile sent to the edge function
// =============================================================================

export interface StudioProfile {
  display_name: string;
  native_language: SourceLanguageCode;
  target_language: TargetLanguageCode;
  level: CefrLevelCode;
  pace: PaceCode;
  topics: TopicCode[];
  learning_reason: LearningReasonCode | null;
  profession: string | null;
  destination_city: string | null;
  destination_country: string | null;
  situations: SituationCode[] | null;
  learning_focus: LearningFocusCode[] | null;
}

const DEFAULT_PROFILE: StudioProfile = {
  display_name: 'Studio',
  native_language: 'en',
  target_language: 'es',
  level: 'b1',
  pace: 'regular',
  topics: ['travel', 'food'],
  learning_reason: null,
  profession: null,
  destination_city: null,
  destination_country: null,
  situations: null,
  learning_focus: null,
};

// =============================================================================
// Generation result from the edge function
// =============================================================================

interface GenerationResult {
  workbook_id: string;
  blocks_generated: number;
  blocks_failed: number;
  duration_ms: number;
  tokens: {
    input: number;
    output: number;
    api_calls: number;
    estimated_cost_usd: number;
  };
  model: string;
  provider: string;
}

// =============================================================================
// Comparison result — side-by-side block from two providers
// =============================================================================

export interface ComparisonResult {
  provider: AIProviderCode;
  model: string;
  block: Block | null;
  error: string | null;
  tokens: {
    input: number;
    output: number;
    api_calls: number;
    estimated_cost_usd: number;
  } | null;
  duration_ms: number;
}

// =============================================================================
// Studio history item
// =============================================================================

export interface StudioWorkbook {
  id: string;
  title: string;
  theme: string;
  block_count: number | null;
  generation_model: string | null;
  generation_metadata: Record<string, unknown> | null;
  created_at: string;
}

// =============================================================================
// Store state
// =============================================================================

interface StudioState {
  // Profile configuration
  profile: StudioProfile;
  setProfile: (profile: Partial<StudioProfile>) => void;
  resetProfile: () => void;

  // Prompt overrides
  systemPromptOverride: string | null;
  setSystemPromptOverride: (override: string | null) => void;
  blockPromptOverrides: Partial<Record<BlockType, string>>;
  setBlockPromptOverride: (type: BlockType, prompt: string | null) => void;
  clearPromptOverrides: () => void;

  // Provider selection
  provider: AIProviderCode;
  model: string;
  setProvider: (p: AIProviderCode) => void;
  setModel: (m: string) => void;

  // Comparison mode
  compareEnabled: boolean;
  compareProvider: AIProviderCode;
  compareModel: string;
  setCompareEnabled: (enabled: boolean) => void;
  setCompareProvider: (p: AIProviderCode) => void;
  setCompareModel: (m: string) => void;
  comparisonResults: ComparisonResult[];

  // Generation state
  generating: boolean;
  generationError: string | null;
  generationResult: GenerationResult | null;

  // Current workbook (loaded after generation or from history)
  workbook: Workbook | null;
  blocks: Block[];

  // Actions
  generate: () => Promise<void>;
  loadWorkbook: (id: string) => Promise<void>;
  setBlocks: (blocks: Block[]) => void;
  updateBlock: (updated: Block) => void;
  deleteBlock: (blockId: string) => void;
  reorderBlocks: (blocks: Block[]) => void;
  saveWorkbook: () => Promise<void>;
  saving: boolean;

  // PDF download
  downloading: boolean;
  downloadPdf: () => Promise<void>;

  // Single block regeneration
  regeneratingBlockId: string | null;
  regenerateBlock: (blockId: string, blockType: BlockType, subTheme: string) => Promise<void>;

  // History
  history: StudioWorkbook[];
  historyLoading: boolean;
  loadHistory: () => Promise<void>;
}

export const useStudioStore = create<StudioState>((set, get) => ({
  // ── Profile ───────────────────────────────────────────────────────
  profile: { ...DEFAULT_PROFILE },
  setProfile: (partial) => set((s) => ({ profile: { ...s.profile, ...partial } })),
  resetProfile: () => set({ profile: { ...DEFAULT_PROFILE } }),

  // ── Provider selection ───────────────────────────────────────────
  provider: DEFAULT_AI_PROVIDER,
  model: DEFAULT_AI_MODEL,
  setProvider: (p) => {
    const models = AI_MODELS[p];
    set({ provider: p, model: models[0]?.id ?? '' });
  },
  setModel: (m) => set({ model: m }),

  // ── Comparison mode ────────────────────────────────────────────
  compareEnabled: false,
  compareProvider: 'openai' as AIProviderCode,
  compareModel: 'gpt-4o',
  setCompareEnabled: (enabled) => set({ compareEnabled: enabled }),
  setCompareProvider: (p) => {
    const models = AI_MODELS[p];
    set({ compareProvider: p, compareModel: models[0]?.id ?? '' });
  },
  setCompareModel: (m) => set({ compareModel: m }),
  comparisonResults: [],

  // ── Prompt overrides ──────────────────────────────────────────────
  systemPromptOverride: null,
  setSystemPromptOverride: (override) => set({ systemPromptOverride: override }),
  blockPromptOverrides: {},
  setBlockPromptOverride: (type, prompt) =>
    set((s) => {
      const overrides = { ...s.blockPromptOverrides };
      if (prompt === null) {
        delete overrides[type];
      } else {
        overrides[type] = prompt;
      }
      return { blockPromptOverrides: overrides };
    }),
  clearPromptOverrides: () => set({ systemPromptOverride: null, blockPromptOverrides: {} }),

  // ── Generation ────────────────────────────────────────────────────
  generating: false,
  generationError: null,
  generationResult: null,
  workbook: null,
  blocks: [],

  generate: async () => {
    const { profile, systemPromptOverride, blockPromptOverrides, provider, model } = get();

    set({
      generating: true,
      generationError: null,
      generationResult: null,
      workbook: null,
      blocks: [],
    });

    try {
      const body: Record<string, unknown> = { profile, provider, model };
      if (systemPromptOverride) {
        body.system_prompt_override = systemPromptOverride;
      }
      if (Object.keys(blockPromptOverrides).length > 0) {
        body.block_prompt_overrides = blockPromptOverrides;
      }

      const { data, error } = await invokeFunction('studio-generate', { body });

      if (error) {
        // invokeFunction now extracts the real error from edge function responses,
        // so error.message contains the actual detail instead of the generic wrapper.
        throw new Error(error.message ?? 'Generation failed');
      }

      const result = data as GenerationResult;
      set({ generationResult: result });

      // Load the generated workbook
      await get().loadWorkbook(result.workbook_id);
      // Refresh history
      await get().loadHistory();
    } catch (err) {
      set({
        generationError: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      set({ generating: false });
    }
  },

  loadWorkbook: async (id: string) => {
    const { data, error } = await supabase.from('workbooks').select('*').eq('id', id).single();

    if (error || !data) {
      console.error('Failed to load workbook:', error?.message);
      return;
    }

    const wb = data as Workbook;
    set({ workbook: wb, blocks: wb.blocks });
  },

  setBlocks: (blocks) => set({ blocks }),

  updateBlock: (updated) =>
    set((s) => ({
      blocks: s.blocks.map((b) => (b.id === updated.id ? updated : b)),
    })),

  deleteBlock: (blockId) =>
    set((s) => ({
      blocks: s.blocks.filter((b) => b.id !== blockId).map((b, i) => ({ ...b, order: i + 1 })),
    })),

  reorderBlocks: (blocks) => set({ blocks: blocks.map((b, i) => ({ ...b, order: i + 1 })) }),

  saving: false,

  saveWorkbook: async () => {
    const { workbook, blocks } = get();
    if (!workbook) return;

    set({ saving: true });

    const { error } = await supabase.from('workbooks').update({ blocks }).eq('id', workbook.id);

    if (error) {
      console.error('Failed to save:', error.message);
    }

    set({ saving: false });
  },

  // ── PDF download ─────────────────────────────────────────────────
  downloading: false,

  downloadPdf: async () => {
    const { workbook, saving } = get();
    if (!workbook || saving) return;

    // Auto-save before rendering to ensure latest blocks are persisted
    await get().saveWorkbook();

    set({ downloading: true });

    try {
      const { data, error } = await invokeFunction<{ signed_url: string }>('render-pdf', {
        body: { workbook_id: workbook.id },
      });

      if (error) {
        throw new Error(error.message ?? 'PDF rendering failed');
      }

      const { signed_url } = data;

      // Fetch as blob so the download attribute works reliably.
      // Browsers ignore `download` on cross-origin URLs (like Supabase Storage
      // signed URLs), so we create a local object URL instead.
      const pdfResponse = await fetch(signed_url);
      if (!pdfResponse.ok) throw new Error('Failed to download PDF');
      const blob = await pdfResponse.blob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${workbook.title ?? 'ellie-workbook'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      console.error('PDF download failed:', err);
      alert(`PDF download failed: ${message}`);
    } finally {
      set({ downloading: false });
    }
  },

  // ── Single block regeneration ─────────────────────────────────────
  regeneratingBlockId: null,

  regenerateBlock: async (blockId, blockType, subTheme) => {
    const {
      profile,
      blocks,
      systemPromptOverride,
      blockPromptOverrides,
      provider,
      model,
      compareEnabled,
      compareProvider,
      compareModel,
    } = get();
    const blockIndex = blocks.findIndex((b) => b.id === blockId);
    if (blockIndex === -1) return;

    set({ regeneratingBlockId: blockId, comparisonResults: [] });

    const buildBody = (p: AIProviderCode, m: string): Record<string, unknown> => {
      const body: Record<string, unknown> = {
        profile,
        block_type: blockType,
        sub_theme: subTheme,
        block_order: blockIndex + 1,
        total_blocks: blocks.length,
        previous_vocabulary: [],
        previous_block_summaries: blocks
          .filter((b) => b.id !== blockId)
          .map((b, i) => `Block ${i + 1}: ${b.type} — "${b.title}"`),
        provider: p,
        model: m,
      };
      if (systemPromptOverride) {
        body.system_prompt_override = systemPromptOverride;
      }
      if (blockPromptOverrides[blockType]) {
        body.block_prompt_override = blockPromptOverrides[blockType];
      }
      return body;
    };

    try {
      if (compareEnabled) {
        // Fire two requests in parallel for side-by-side comparison
        const startA = Date.now();
        const startB = Date.now();

        const [resultA, resultB] = await Promise.all([
          invokeFunction<{
            block: Block;
            tokens: {
              input: number;
              output: number;
              api_calls: number;
              estimated_cost_usd: number;
            };
            provider: string;
            model: string;
          }>('studio-generate-block', { body: buildBody(provider, model) }).then((res) => ({
            duration_ms: Date.now() - startA,
            ...res,
          })),
          invokeFunction<{
            block: Block;
            tokens: {
              input: number;
              output: number;
              api_calls: number;
              estimated_cost_usd: number;
            };
            provider: string;
            model: string;
          }>('studio-generate-block', { body: buildBody(compareProvider, compareModel) }).then(
            (res) => ({
              duration_ms: Date.now() - startB,
              ...res,
            }),
          ),
        ]);

        const comparisons: ComparisonResult[] = [
          {
            provider,
            model,
            block: resultA.data?.block ?? null,
            error: resultA.error?.message ?? null,
            tokens: resultA.data?.tokens ?? null,
            duration_ms: resultA.duration_ms,
          },
          {
            provider: compareProvider,
            model: compareModel,
            block: resultB.data?.block ?? null,
            error: resultB.error?.message ?? null,
            tokens: resultB.data?.tokens ?? null,
            duration_ms: resultB.duration_ms,
          },
        ];

        set({ comparisonResults: comparisons });

        // Use the primary provider's result for the block list
        if (resultA.data?.block) {
          const newBlock = resultA.data.block;
          set((s) => ({
            blocks: s.blocks.map((b) =>
              b.id === blockId ? { ...newBlock, order: blockIndex + 1 } : b,
            ),
          }));
        }
      } else {
        // Standard single-provider regeneration
        const { data, error } = await invokeFunction<{ block: Block }>('studio-generate-block', {
          body: buildBody(provider, model),
        });

        if (error) {
          throw new Error(error.message ?? 'Block regeneration failed');
        }

        const newBlock = data.block;
        set((s) => ({
          blocks: s.blocks.map((b) =>
            b.id === blockId ? { ...newBlock, order: blockIndex + 1 } : b,
          ),
        }));
      }
    } catch (err) {
      console.error('Block regeneration failed:', err);
    } finally {
      set({ regeneratingBlockId: null });
    }
  },

  // ── History ───────────────────────────────────────────────────────
  history: [],
  historyLoading: false,

  loadHistory: async () => {
    set({ historyLoading: true });

    const { data, error } = await supabase
      .from('workbooks')
      .select('id, title, theme, block_count, generation_model, generation_metadata, created_at')
      .eq('is_studio', true)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error('Failed to load history:', error.message);
    }

    set({
      history: (data as StudioWorkbook[] | null) ?? [],
      historyLoading: false,
    });
  },
}));
